import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useAtomValue } from "jotai";
import { environment } from "../environment";
import { loggedInUserAtom } from "../services/login/types";
import { canSeePastMaintenance } from "../utils/permissions";

const POLL_INTERVAL_MS = 5_000;
const FLAG_NAME = "website-under-maintainance";

const getMaintenanceFlagValue = (flags: any[]): boolean => {
  const flag = flags.find((f: any) => f.flag_name === FLAG_NAME);
  if (!flag) return false;
  if (
    process.env.REACT_APP_ENVIRONMENT === "development" ||
    process.env.REACT_APP_ENVIRONMENT === "test"
  ) {
    return flag.development_value;
  }
  return flag.production_value;
};

type Props = {
  children: React.ReactNode;
};

export const MaintenanceGuard = ({ children }: Props) => {
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const user = useAtomValue(loggedInUserAtom);
  const bypassMaintenance = canSeePastMaintenance(user);

  const checkMaintenance = useCallback(async () => {
    try {
      const response = await axios.get(
        `${environment.authenticationUrl}/feature-flags`
      );
      const maintenanceStatus = getMaintenanceFlagValue(response.data);
      setIsUnderMaintenance(maintenanceStatus);

      // Only continue polling if under maintenance
      if (maintenanceStatus) {
        if (!intervalRef.current) {
          intervalRef.current = setInterval(checkMaintenance, POLL_INTERVAL_MS);
        }
      } else {
        // Stop polling if not under maintenance
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch {
      setIsUnderMaintenance(false);
      // Stop polling on error
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } finally {
      setInitialCheckDone(true);
    }
  }, []);

  useEffect(() => {
    // Initial check on mount
    checkMaintenance();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkMaintenance]);

  if (!initialCheckDone) return null;

  if (isUnderMaintenance && !bypassMaintenance) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-surface-raised">
        <div className="text-center px-6 max-w-md">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500 mb-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.049.58.025 1.193-.14 1.743"
            />
          </svg>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
            Under Maintenance
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            We&apos;re currently performing scheduled maintenance. We&apos;ll be
            back shortly.
          </p>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <svg
              className="h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Checking availability...
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
